/**
 * @overview SegmentedControl
 * @stage Proposed
 * @author Maciek (@pekala)
 * @designer Annika
 * @spec
 */

import {useId} from '@reach/auto-id'
import styled, {css} from 'styled-components'

import {__alphaTokens as tokens, focusRingSibling} from '@pleo-io/telescope'

type SegmentedControlOption<Value extends string> = {
    value: Value
    label: string
}

interface SegmentedControlProps<Value extends string> {
    /**
     * HTML id passed to outer most element
     */
    id?: string

    /**
     * HTML name passed to input elements
     */
    name?: string

    /**
     * An array of options, with value and label
     */
    options: SegmentedControlOption<Value>[]

    /**
     * Currently selected value
     *
     * - string, which should be one of provided SegmentedControlOption' values
     * - null, which means that no option is selected
     */
    value: Value | null

    /**
     * Callback called with the selected value
     * @default noop
     */
    onChange: (newValue: Value) => void

    /**
     * font-size override
     *
     */
    fontSize?: string

    /**
     * Full width
     */
    fullWidth?: boolean
    /**
     * Text align for option labels
     */
    textAlign?: 'left' | 'center'
}

/**
 * Radio group element styled as an inline toggle with multiple options
 * Use instead of a select element if there are few options or instead
 * of a checkbox if options are not strictly on/off
 */
export function SegmentedControl<Value extends string>({
    options,
    value,
    name,
    id,
    onChange = () => {},
    fontSize,
    fullWidth,
    textAlign = 'left',
}: SegmentedControlProps<Value>) {
    const groupName = useId()
    return (
        <Wrapper id={id} textAlign={textAlign} data-generic-ui="radio-switch">
            {options.map((option) => (
                <Option<Value>
                    key={option.value ?? option.label}
                    value={option.value}
                    label={option.label}
                    isSelected={option.value === value}
                    groupName={name ?? groupName}
                    onChange={onChange}
                    fontSize={fontSize}
                    grow={fullWidth}
                />
            ))}
        </Wrapper>
    )
}

interface OptionProps<Value extends string> {
    value: Value
    label: string
    isSelected: boolean
    groupName?: string
    onChange: (value: Value) => void
    fontSize?: string
    grow?: boolean
}
function Option<Value extends string>({
    value,
    label,
    isSelected,
    onChange,
    groupName,
    fontSize,
    grow,
}: OptionProps<Value>) {
    const id = useId()
    return (
        <OptionWrapper $checked={isSelected} grow={grow}>
            <HiddenRadioInput
                type="radio"
                id={id}
                value={value}
                name={groupName}
                tabIndex={0}
                onChange={() => onChange(value)}
                checked={isSelected}
            />
            <Label htmlFor={id} style={{fontSize: String(fontSize)}}>
                {label}
            </Label>
        </OptionWrapper>
    )
}

interface ToggleSwitchProps {
    /**
     * HTML id passed to outer most element
     */
    id?: string

    /**
     * HTML name passed to input elements
     */
    name?: string

    /**
     * Label used for the true, i.e. "on" value
     */
    onLabel: string

    /**
     * Label used for the false, i.e. "off" value
     */
    offLabel: string

    /**
     * Currently selected value
     */
    value: boolean | null

    /**
     * Callback called with the selected value
     * @default noop
     */
    onChange: (newValue: boolean) => void
}

/**
 * Toggle switch element, simplifies usage of Radio Switch for boolean values
 * Use instead of a checkbox if options are not strictly on/off
 */
export const ToggleSwitch = ({name, id, onLabel, offLabel, value, onChange}: ToggleSwitchProps) => {
    type OnOff = 'on' | 'off'

    const options = [
        {value: 'on' as const, label: onLabel},
        {value: 'off' as const, label: offLabel},
    ]

    let switchValue: OnOff | null = null
    if (value !== null) {
        switchValue = value ? 'on' : 'off'
    }

    return (
        <SegmentedControl<OnOff>
            id={id}
            name={name}
            options={options}
            onChange={(val) => onChange(val === 'on')}
            value={switchValue}
        />
    )
}

////////////////////////////////////////////////////////////////////////////////

const Wrapper = styled.div<{textAlign?: 'left' | 'center'}>`
    display: flex;

    ${({textAlign}) =>
        textAlign &&
        css`
            text-align: ${textAlign};
        `}
`
const OptionWrapper = styled.div<{$checked?: boolean; grow?: boolean}>`
    border: ${tokens.sizeBorderDefault} solid ${tokens.colorBorderInteractiveQuiet};
    border-left: none;
    cursor: pointer;
    transition: ${tokens.fastIn};
    transition-property: background-color, color, border-color;
    color: ${tokens.colorContentInteractive};

    ${({grow}) =>
        grow &&
        css`
            flex-grow: 1;
        `}

    &:first-child {
        border-top-left-radius: ${tokens.arc4};
        border-bottom-left-radius: ${tokens.arc4};
        border: ${tokens.sizeBorderDefault} solid ${tokens.colorBorderInteractiveQuiet};

        ${({$checked}) =>
            $checked &&
            css`
                border-color: ${tokens.colorBorderInteractiveLoud};
            `}
    }

    &:last-child {
        border-top-right-radius: ${tokens.arc4};
        border-bottom-right-radius: ${tokens.arc4};
    }

    &:hover {
        background-color: ${tokens.colorBackgroundInteractiveQuietHover};
    }

    ${({$checked}) =>
        $checked &&
        css`
            background-color: ${tokens.colorBackgroundInteractiveSelected};
            color: ${tokens.colorContentInteractiveSelected};
            border-color: ${tokens.colorBorderInteractiveSelected};

            &:hover {
                background-color: ${tokens.colorBackgroundInteractiveSelectedHover};
                color: ${tokens.colorContentInteractiveSelectedHover};
                border-color: ${tokens.colorBorderInteractiveSelectedHover};
            }
        `}
`
const HiddenRadioInput = styled.input`
    opacity: 0;
    position: absolute;
    cursor: pointer;
`
const Label = styled.label`
    display: inline-block;
    padding: ${tokens.spacing8} ${tokens.spacing20};
    line-height: ${tokens.lineHeight1};
    font-size: ${tokens.fontMedium};
    cursor: pointer;
    ${focusRingSibling(HiddenRadioInput)}
`
